import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from 'api/entityApi'
import { getSsoSession } from 'auth/ssoClient'
import { buildBrief, type Brief, type BriefSources } from './buildBrief'
import { MEMORY_ENABLED, fetchSnapshot, putSnapshot } from './memoryApi'
import { EDITIONS_ENABLED, fetchEditionEvents } from './editionsApi'

/**
 * Loads what the brief reads, all at once, when the record opens.
 *
 * The tabs below fetch on first click, which is right for a table nobody may look at. The brief
 * is the opposite: it is the first thing read, so it cannot wait for clicks. Every channel is
 * requested in parallel and each is allowed to fail or run long on its own - one slow endpoint
 * costs the brief that channel (and says so), never the whole panel.
 *
 * Since sql/330 the record also remembers what its last read produced. That snapshot is fetched
 * first (one quick call) and shown at once, dated "as of ...", while the channels are re-read
 * behind it; when they are all in, the brief is rebuilt from the live rows and the snapshot is
 * replaced if anything changed. A snapshot younger than FRESH_MS (the same person re-opening the
 * record) is trusted outright and nothing is re-read - the reload button always re-reads.
 *
 * The re-read is still a FULL read: none of the three history APIs accepts a "since" time. The
 * day they do, this is the one place to pass the snapshot's readUpTo and merge instead.
 *
 * Two of the channels are slow on the server (the WhatsApp conversation ~9 s, the call log ~4 s);
 * the rest answer in about a second. Waiting for the slowest would hold back everything the others
 * already know, so the brief is shown once at least half the channels are in, and is rebuilt as
 * each late one arrives. While any is outstanding the panel names it as "still reading", so a line
 * that later changes was never presented as final.
 */

/** Longest the brief waits for one channel. The Calls endpoint has been seen at ~14 s. */
const CHANNEL_TIMEOUT_MS = 20_000
/** A snapshot this young is shown as-is with no re-read. */
const FRESH_MS = 2 * 60_000

type ChannelKey = Exclude<keyof BriefSources, 'nameHints'>

function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>, outer: AbortSignal): Promise<T> {
  const controller = new AbortController()
  const abort = () => controller.abort()
  outer.addEventListener('abort', abort)
  const timer = window.setTimeout(abort, CHANNEL_TIMEOUT_MS)
  return run(controller.signal).finally(() => {
    window.clearTimeout(timer)
    outer.removeEventListener('abort', abort)
  })
}

/** One channel being read, for the loader: real progress, not a spinner's pretence of it. */
export interface ChannelProgress {
  key: string
  label: string
  done: boolean
}

export interface EntityBriefState {
  brief: Brief | null
  loading: boolean
  /** Channels not in yet while a first brief is already on screen. Empty once everything has answered. */
  stillReading: string[]
  progress: ChannelProgress[]
  /** When what is on screen was read: the snapshot's time until the live read completes, then now. */
  asOf: Date | null
  /** A snapshot is on screen and the channels are being re-read behind it. */
  refreshing: boolean
  reload: () => void
}

const PROGRESS_LABEL: Record<string, string> = {
  calls: 'Calls',
  whatsapp: 'WhatsApp',
  interactions: 'Follow-ups',
  notes: 'Notes',
  tasks: 'Tasks',
  appointments: 'Appointments',
  deals: 'Deals',
  editions: 'Editions',
}

export function useEntityBrief(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  enabled: boolean,
  nameHints: string[] = [],
): EntityBriefState {
  const [sources, setSources] = useState<BriefSources | null>(null)
  const [stillReading, setStillReading] = useState<string[]>([])
  const [loading, setLoading] = useState(enabled)
  const [nonce, setNonce] = useState(0)
  const [progress, setProgress] = useState<ChannelProgress[]>([])
  const [asOf, setAsOf] = useState<Date | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (!enabled || !entityId) {
      setLoading(false)
      return
    }

    const outer = new AbortController()
    let active = true
    setLoading(true)
    setSources(null)
    setAsOf(null)
    setRefreshing(false)

    const isLead = entityType === 'lead'
    const forced = nonce > 0 // the reload button: always re-read, whatever the snapshot's age

    // Follow-ups, notes, tasks and appointments are keyed by LeadId on the API. An enquiry id is
    // not a lead id - sending one would read some other customer's history into this brief - so
    // for an enquiry only the two channels that take the entity's own type are read.
    const loaders: Array<[ChannelKey, (signal: AbortSignal) => Promise<unknown[]>]> = [
      ['calls', (s) => api.fetchCallLogs(entityId, entityType, s)],
      [
        'whatsapp',
        async (s) => {
          const convUserId = await api.fetchWaConversationUserId(entityType, entityId, s)
          return convUserId ? api.fetchWaChatLog(entityType, entityId, convUserId, s) : []
        },
      ],
    ]
    if (isLead) {
      loaders.push(
        ['interactions', (s) => api.fetchInteractionHistories(entityId, s)],
        ['notes', (s) => api.fetchNotes(entityId, s)],
        ['tasks', (s) => api.fetchTasks(entityId, s)],
        ['appointments', (s) => api.fetchAppointments(entityId, s)],
        // the record load is already cached by the page, so this costs no request
        ['deals', (s) => api.fetchPipelineProgress(entityId, s)],
      )
      // what happened to the lead in the editions: interest in a unit, holds, bookings, visits,
      // the agreement, the workflow engine's events - read only where the editions service exists
      if (EDITIONS_ENABLED) loaders.push(['editions', (s) => fetchEditionEvents(entityType, entityId, s)])
    }

    setProgress(loaders.map(([key]) => ({ key, label: PROGRESS_LABEL[key] ?? key, done: false })))
    setStillReading([])

    // Channels not read for this entity type are empty by definition, not unavailable. The Chat
    // tab is left out on purpose: its API returns one undated transcript blob per record, which
    // cannot be placed on a timeline or attributed to a side.
    const arrived: BriefSources = {
      interactions: [], calls: [], whatsapp: [], chats: [], notes: [], tasks: [], appointments: [], deals: [], editions: [],
    }
    const outstanding = new Set<string>(loaders.map(([key]) => key))
    const showAfter = Math.ceil(loaders.length / 2)

    let snapshotShown = false
    let snapshotJson: string | null = null

    const tick = (key: ChannelKey, rows: unknown[] | null) => {
      if (!active) return
      ;(arrived as Record<ChannelKey, unknown[] | null>)[key] = rows
      outstanding.delete(key)
      setProgress((prev) => prev.map((c) => (c.key === key ? { ...c, done: true } : c)))
      // With a snapshot on screen, half-read live rows must not replace it: the snapshot is whole
      // and dated; a half read is neither. Without one, show as soon as half the channels are in.
      if (!snapshotShown && loaders.length - outstanding.size >= showAfter) {
        // a channel still outstanding reads as empty for now - and is named as still being read
        setSources({ ...arrived })
        setStillReading([...outstanding].map((k) => PROGRESS_LABEL[k] ?? k))
        setLoading(false)
      }
    }

    const readLive = () =>
      Promise.all(
        loaders.map(([key, run]) =>
          withTimeout(run, outer.signal)
            .then(
              (rows) => [key, rows] as const,
              () => [key, null] as const, // failed or timed out: unknown, not empty
            )
            .then((result) => {
              tick(result[0], result[1])
              return result
            }),
        ),
      ).then(() => {
        if (!active) return
        const live = { ...arrived }
        setSources(live)
        setStillReading([])
        setRefreshing(false)
        setLoading(false)
        setAsOf(new Date())
        // remember this read for the next open - only when something changed, and only what was read
        const liveJson = JSON.stringify(live)
        if (MEMORY_ENABLED && liveJson !== snapshotJson) {
          const unavailable = (Object.keys(live) as ChannelKey[]).filter((k) => live[k] === null)
          const newest = newestWallClock(live)
          const count = (Object.values(live) as Array<unknown[] | null>).reduce((n, rows) => n + (rows?.length ?? 0), 0)
          putSnapshot(entityType, entityId, live, newest, count, unavailable).catch(() => {
            /* best effort: the next open reads live again, nothing else is affected */
          })
        }
      })

    if (!MEMORY_ENABLED) {
      void readLive()
    } else {
      // the snapshot first: one quick call, then the live read behind it (or not at all if it is fresh)
      fetchSnapshot(entityType, entityId, outer.signal)
        .catch(() => null)
        .then((snap) => {
          if (!active) return
          if (snap) {
            snapshotShown = true
            snapshotJson = JSON.stringify(snap.sources)
            setSources(snap.sources)
            setAsOf(snap.computedAt)
            setLoading(false)
            setStillReading([])
            const fresh = Date.now() - snap.computedAt.getTime() < FRESH_MS
            if (fresh && !forced) {
              setProgress((prev) => prev.map((c) => ({ ...c, done: true })))
              return
            }
            setRefreshing(true)
          }
          void readLive()
        })
    }

    return () => {
      active = false
      outer.abort()
    }
  }, [entityType, entityId, enabled, nonce])

  const brief = useMemo(() => {
    if (!sources) return null
    const session = getSsoSession()
    return buildBrief({ ...sources, nameHints }, { loginName: session.loginName, displayName: session.displayName }, new Date())
    // nameHints is a fresh array each render; its content is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, nameHints.join('|')])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { brief, loading, stillReading, progress, asOf, refreshing, reload }
}

const two = (n: number) => String(n).padStart(2, '0')

/** The newest dated entry across the channels, as the account's wall clock with no zone. */
function newestWallClock(src: BriefSources): string | null {
  const brief = buildBrief(src, { loginName: '', displayName: '' }, new Date())
  const d = brief.timeline.find((t) => t.when)?.when
  return d ? `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}` : null
}
